import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { format } from "date-fns";
import { environment } from "environment";
import { Observable, map, of } from "rxjs";
import { PatientListRequest } from "../../features/patients/patient-list/patient-list-request.interface";
import { DateFormat } from "../constants/core-constants";
import { PatientFilterType } from "../enums/patient-filter.enum";
import { DropdownItem } from "../interfaces/dropdown-item.interface";
import { ListResponse } from "../interfaces/list-response.interface";
import {
  ActiveTherapist,
  CoTxBillingTimeCheck,
  FilteredPatientListItem,
  Patient,
  PatientFacilityAdmission,
  PatientFile,
  PatientHeader,
  PatientListItem,
  PatientSyncCandidate,
  PatientTherapyAdmission,
  TherapyBillingUserTimeDetails,
  TherapyBillingUserTimeDetailsReadonly,
  TherapyBillingUserTimeDetailsSave,
  UserDisciplines,
} from "../interfaces/patient.interface";
import { sanitizeParams } from "../sanitize-params";
import { RCHttpResponse } from "./rc-http-response";

@Injectable({ providedIn: "root" })
export class PatientClient {
  endpoint = "patient";

  constructor(private httpClient: HttpClient) {}

  getPatients(
    filters: PatientListRequest
  ): Observable<ListResponse<PatientListItem>> {
    const params = sanitizeParams(filters) as Record<string, never>;

    return this.httpClient.get<ListResponse<PatientListItem>>(
      `${environment.endpoint}/${this.endpoint}/list`,
      { params }
    );
  }

  getPatientsForecastList(
    filters: PatientListRequest
  ): Observable<ListResponse<PatientListItem>> {
    const params = sanitizeParams(filters) as Record<string, never>;

    return this.httpClient.get<ListResponse<PatientListItem>>(
      `${environment.endpoint}/${this.endpoint}/get-forecast-patientlist`,
      { params }
    );
  }

  getPatient(patientId: string): Observable<Patient> {
    return this.httpClient.get<Patient>(
      `${environment.endpoint}/${this.endpoint}/get/${patientId}`
    );
  }

  savePatient(patient: Patient): Observable<Patient> {
    if (patient.id) {
      return this.httpClient.put<Patient>(
        `${environment.endpoint}/${this.endpoint}/edit`,
        patient
      );
    }
    return this.httpClient.post<Patient>(
      `${environment.endpoint}/${this.endpoint}/add`,
      patient
    );
  }

  getDuplicatePatients(patient: Patient): Observable<Patient[]> {
    const params = sanitizeParams(patient) as Record<string, never>;
    return this.httpClient
      .get<{
        responseData: Patient[];
      }>(
        `${environment.endpoint}/${this.endpoint}/validate-get-duplicate-patients`,
        {
          params,
        }
      )
      .pipe(map((response: { responseData: any }) => response.responseData));
  }

  getFacilityAdmissions(patientId: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-facility-admissions/${patientId}`
    );
  }

  addFacilityAdmission(
    patientId: string,
    facilityAdmission: PatientFacilityAdmission
  ): Observable<PatientFacilityAdmission> {
    return this.httpClient.post<PatientFacilityAdmission>(
      `${environment.endpoint}/${this.endpoint}/add-facility-admission`,
      { ...facilityAdmission, patientid: patientId }
    );
  }

  deleteFacilityAdmission(
    facilityAdmissionId: string
  ): Observable<PatientFacilityAdmission> {
    return this.httpClient.delete<PatientFacilityAdmission>(
      `${environment.endpoint}/${this.endpoint}/delete-facility-admission/${facilityAdmissionId}`
    );
  }

  checkFacilityAdmissionDelete(
    facilityAdmissionId: number
  ): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${environment.endpoint}/${this.endpoint}/get-facility-admission-delete-confirm/${facilityAdmissionId}`
    );
  }

  editFacilityAdmission(
    facilityAdmission: PatientFacilityAdmission
  ): Observable<RCHttpResponse<void>> {
    return this.httpClient.put<RCHttpResponse<void>>(
      `${environment.endpoint}/${this.endpoint}/edit-facility-admission`,
      facilityAdmission
    );
  }

  getFacilityAdmission(
    facilityAdmissionId: string
  ): Observable<PatientFacilityAdmission> {
    return this.httpClient.get<PatientFacilityAdmission>(
      `${environment.endpoint}/${this.endpoint}/get-facility-admission/${facilityAdmissionId}`
    );
  }

  uploadFile(formData: FormData): Observable<string> {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/upload-file`,
      formData,
      {
        responseType: "text",
      }
    );
  }

  addFile(patientFile: PatientFile): Observable<unknown> {
    return this.httpClient.post<unknown>(
      `${environment.endpoint}/${this.endpoint}/add-file`,
      patientFile
    );
  }

  getFiles(patientId: string): Observable<PatientFile[]> {
    return this.httpClient.get<PatientFile[]>(
      `${environment.endpoint}/${this.endpoint}/files?patientid=${patientId}`
    );
  }

  deleteFile(fileId: string): Observable<unknown> {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete-patient-file/${fileId}`
    );
  }

  getLatestAdmission(patientId: string): Observable<PatientFacilityAdmission> {
    return this.httpClient.get<PatientFacilityAdmission>(
      `${environment.endpoint}/${this.endpoint}/get-latest-facility-admission/${patientId}`
    );
  }

  deleteTherapyAdmission(
    facilityAdmissionId: string
  ): Observable<PatientTherapyAdmission> {
    return this.httpClient.delete<PatientTherapyAdmission>(
      `${environment.endpoint}/${this.endpoint}/delete-therapy-admission/${facilityAdmissionId}`
    );
  }

  editTherapyAdmission(
    facilityAdmission: PatientTherapyAdmission,
    facilityid: string
  ): Observable<PatientTherapyAdmission> {
    return this.httpClient.put<PatientTherapyAdmission>(
      `${environment.endpoint}/${this.endpoint}/edit-therapy-admission`,
      { ...facilityAdmission, facilityid }
    );
  }

  getTherapyAdmission(
    therapyAdmissionId: string
  ): Observable<PatientTherapyAdmission> {
    return this.httpClient.get<PatientTherapyAdmission>(
      `${environment.endpoint}/${this.endpoint}/get-therapy-admission/${therapyAdmissionId}`
    );
  }

  getTherapyAdmissions(
    patientId: string,
    disciplineId: string
  ): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-therapy-admissions/${patientId}/${disciplineId}`
    );
  }

  getPatientDisciplines(patientId: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-disciplines/${patientId}`
    );
  }

  addTherapyAdmission(
    patientid: string,
    therapyAdmission: PatientTherapyAdmission,
    facilityid: string,
    disciplineid: number | null = null,
    initiatepoc: boolean = false
  ): Observable<PatientTherapyAdmission> {
    let obj = {
      ...therapyAdmission,
      patientid,
      facilityid,
      initiatepoc,
    } as any;

    if (disciplineid) {
      obj.disciplineid = disciplineid;
    }

    return this.httpClient.post<PatientTherapyAdmission>(
      `${environment.endpoint}/${this.endpoint}/add-therapy-admission`,
      obj
    );
  }

  getTherapyAdmissionsByFacilityAdmissionId(
    patientId: string,
    facilityAdmissionId: number
  ) {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-therapy-admissions-by-facility-admission/${patientId}/${facilityAdmissionId}`
    );
  }

  getHeaderInfo(patientId: string): Observable<PatientHeader> {
    return this.httpClient.get<PatientHeader>(
      `${environment.endpoint}/${this.endpoint}/get-header/${patientId}`
    );
  }

  getTherapists(
    facilityId: string,
    date: Date,
    disciplineId?: number
  ): Observable<DropdownItem[]> {
    const params: HttpParams = new HttpParams({
      fromObject: sanitizeParams({
        facilityid: facilityId,
        date: format(date, "yyyy-MM-dd"),
        disciplineid: disciplineId,
      }),
    });
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-therapists`,
      {
        params,
      }
    );
  }

  editPatientForecastLastEstimatedTxDate(
    patientFacilityId: string,
    disciplineId: number,
    lastEstimatedTxDate: Date
  ) {
    const date = lastEstimatedTxDate
      ? format(lastEstimatedTxDate, DateFormat)
      : "";

    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/edit-patient-forecast-last-estimated-tx?patientfacilityid=${patientFacilityId}&disciplineid=${disciplineId}&estimatedlasttx=${date}`,
      {}
    );
  }

  getFilteredPatients(
    facilityId: string,
    filterBy: PatientFilterType,
    search: string,
    sort: { property: "name" | "dob"; direction: "ASC" | "DESC" },
    searchDate: string | null,
    type: string,
    includePayor: boolean = false
  ): Observable<ListResponse<FilteredPatientListItem>> {
    const params: HttpParams = new HttpParams({
      fromObject: sanitizeParams({
        Facilityid: facilityId,
        FilterBy: filterBy,
        Keyword: search,
        SortBy: sort.property,
        SortDir: sort.direction,
        SearchDate: searchDate,
        PageType: type,
        IncludePayor: includePayor ? "true" : "false",
      }),
    });
    return this.httpClient.get<ListResponse<FilteredPatientListItem>>(
      `${environment.endpoint}/${this.endpoint}/getAllPatients`,
      {
        params,
      }
    );
  }

  addPatientNote(patientId: string, facilityId: string, note: string) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-patient-notes`,
      {
        patientid: patientId,
        facilityid: facilityId,
        patientnotes: note,
      }
    );
  }

  getPatientNote(
    patientId: string,
    facilityId: string,
    patientFacilityId: number
  ): Observable<string> {
    if (!patientFacilityId || !facilityId || !patientId) {
      return of("");
    }

    return this.httpClient.get<string>(
      `${environment.endpoint}/${this.endpoint}/get-patient-note?patientid=${patientId}&facilityid=${facilityId}&patientfacilityid=${patientFacilityId}`,
      { responseType: "text" as "json" }
    );
  }

  getActiveTherapistsByDate(
    dateString: Date,
    facilityId: string,
    disciplineId?: number
  ): Observable<ActiveTherapist[]> {
    let url = `${environment.endpoint}/${
      this.endpoint
    }/get-active-therapists-by-date?dateString=${format(
      dateString,
      "yyyy-MM-dd"
    )}&facilityid=${facilityId}`;

    if (disciplineId) {
      url += `&disciplineid=${disciplineId}`;
    }

    return this.httpClient.get<ActiveTherapist[]>(url);
  }

  syncWithForecast(patientObj: {
    facilityid: string;
    agendadate: string;
    patienttherapyagendaid: number | undefined | null;
  }) {
    if (patientObj.patienttherapyagendaid === undefined) {
      patientObj.patienttherapyagendaid = null;
    }

    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/sync-forecast-with-agenda`,
      patientObj
    );
  }

  getPatientsToSync(
    facilityId: string,
    date: Date
  ): Observable<PatientSyncCandidate[]> {
    return this.httpClient.get<PatientSyncCandidate[]>(
      `${environment.endpoint}/${this.endpoint}/get-agenda-patients-to-sync-forecast`,
      {
        params: {
          facilityid: facilityId,
          dateString: format(date, "yyyy-MM-dd"),
        },
      }
    );
  }

  getPatientsByAgendaDate(
    facilityId: string,
    date: Date,
    disicplineId: number
  ): Observable<Patient[]> {
    return this.httpClient.get<Patient[]>(
      `${environment.endpoint}/${this.endpoint}/get-agenda-patient-by-discipline`,
      {
        params: {
          facilityid: facilityId,
          dateString: format(date, "yyyy-MM-dd"),
          disciplineid: disicplineId,
        },
      }
    );
  }

  getUserDisciplines(
    userId: string,
    facilityId: string
  ): Observable<UserDisciplines> {
    return this.httpClient.get<UserDisciplines>(
      `${environment.endpoint}/${this.endpoint}/get-user-disciplines`,
      {
        params: {
          userid: userId,
          facilityid: facilityId,
        },
      }
    );
  }

  checkUserBillingData(
    userId: string,
    patientId: string,
    disciplineid: number,
    date: string
  ) {
    const params = sanitizeParams({
      userId,
      patientId,
      disciplineid,
      date,
    }) as Record<string, any>;

    return this.httpClient.get(
      `${environment.endpoint}/${this.endpoint}/check-user-billing-data`,
      {
        params,
      }
    );
  }

  getTotalForecastedMinutes(
    patientId: string,
    date: string,
    disciplineid: number
  ): Observable<
    { totalforecastedminutes: number; modeid: number; mode: string }[]
  > {
    return this.httpClient.get<
      { totalforecastedminutes: number; modeid: number; mode: string }[]
    >(`${environment.endpoint}/${this.endpoint}/get-total-forecasted-minutes`, {
      params: {
        patientid: patientId,
        date: date,
        disciplineid: disciplineid,
      },
    });
  }

  getTotalRemainingMinutes(
    patienttherapyid: number,
    date: string,
    totalINDforecastedmins: number,
    totalCONforecastedmins: number,
    totalGRPforecastedmins: number
  ): Observable<{
    remainingGRPmins: number;
    remainingCONmins: number;
    remainingINDmins: number;
  }> {
    return this.httpClient.get<{
      remainingGRPmins: number;
      remainingCONmins: number;
      remainingINDmins: number;
    }>(
      `${environment.endpoint}/${this.endpoint}/get-remaining-forecasted-minutes`,
      {
        params: {
          patienttherapyid: patienttherapyid.toString(),
          date: date,
          totalINDforecastedmins: totalINDforecastedmins,
          totalCONforecastedmins: totalCONforecastedmins,
          totalGRPforecastedmins: totalGRPforecastedmins,
        },
      }
    );
  }

  getTherapyBillingUserTimeDetails(
    userid: string,
    patientid: string,
    disciplineid: number,
    date: string
  ): Observable<TherapyBillingUserTimeDetails> {
    return this.httpClient.get<TherapyBillingUserTimeDetails>(
      `${environment.endpoint}/${this.endpoint}/get-therapy-billing-user-time-details`,
      {
        params: {
          userid,
          patientid,
          disciplineid: disciplineid.toString(),
          date,
        },
      }
    );
  }

  getTherapyBillingCPTReadonly(
    patientid: string,
    disciplineid: number,
    date: string
  ): Observable<TherapyBillingUserTimeDetailsReadonly> {
    return this.httpClient.get<TherapyBillingUserTimeDetailsReadonly>(
      `${environment.endpoint}/${this.endpoint}/get-user-time-cpt-details`,
      {
        params: {
          patientid,
          disciplineid: disciplineid.toString(),
          date,
        },
      }
    );
  }

  addUpdateTherapyBillingUserTimeDetails(
  therapyBillingUserTimeDetails: TherapyBillingUserTimeDetailsSave
  ) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-update-therapy-billing-user-time`,
      therapyBillingUserTimeDetails
    );
  }

  checkCompletedDcDocStatus(
    therapyBillingUserTimeDetails: TherapyBillingUserTimeDetailsSave
  ) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/check-completed-dc-doc-status`,
      therapyBillingUserTimeDetails
    );
  }
  
  getCPTBillingUsersList(
    patienttherapyid: number,
    cptcodeid: number,
    date: string
  ) {
    return this.httpClient.get(
      `${environment.endpoint}/${this.endpoint}/get-cpt-billing-users-list`,
      {
        params: {
          patienttherapyid: patienttherapyid.toString(),
          cptcodeid: cptcodeid.toString(),
          date,
        },
      }
    );
  }

  checkCoTxUserBillingTime(
    patientId: string,
    facilityId: string,
    date: string,
    disciplineId: number,
    currentCoTxTime: number,
    currentuserid: string
  ): Observable<CoTxBillingTimeCheck> {
    const currentCoTxtime = currentCoTxTime ? currentCoTxTime.toString() : null;

    let params = {
      patientid: patientId,
      facilityid: facilityId,
      date,
      currentuserid,
      disciplineid: disciplineId.toString(),
    } as any;

    if (currentCoTxtime) {
      params = {
        ...params,
        currentCoTxtime,
      };
    }
    return this.httpClient.get<CoTxBillingTimeCheck>(
      `${environment.endpoint}/${this.endpoint}/check-co-tx-billing-user-time`,
      {
        params,
      }
    );
  }
}
